/**
 * Pantalla completa del panel (compartida entre vistas).
 * Se pide sobre documentElement para que al cambiar de ruta no se pierda.
 */
import { reactive } from 'vue';

export const panelFullscreen = reactive({
    active: false,
});

let listening = false;

function readFullscreenActive() {
    if (typeof document === 'undefined') return false;
    return !!(document.fullscreenElement || document.webkitFullscreenElement);
}

function syncFromDocument() {
    panelFullscreen.active = readFullscreenActive();
}

function ensureListening() {
    if (listening || typeof document === 'undefined') return;
    listening = true;
    document.addEventListener('fullscreenchange', syncFromDocument);
    document.addEventListener('webkitfullscreenchange', syncFromDocument);
    syncFromDocument();
}

export function isPanelFullscreen() {
    ensureListening();
    return panelFullscreen.active;
}

export async function enterPanelFullscreen() {
    ensureListening();
    if (readFullscreenActive()) {
        panelFullscreen.active = true;
        return true;
    }
    const root = typeof document !== 'undefined' ? document.documentElement : null;
    if (!root) return false;
    try {
        if (root.requestFullscreen) await root.requestFullscreen();
        else if (root.webkitRequestFullscreen) await root.webkitRequestFullscreen();
        else return false;
        syncFromDocument();
        return panelFullscreen.active;
    } catch {
        syncFromDocument();
        return false;
    }
}

export async function exitPanelFullscreen() {
    ensureListening();
    if (!readFullscreenActive()) {
        panelFullscreen.active = false;
        return true;
    }
    try {
        if (document.exitFullscreen) await document.exitFullscreen();
        else if (document.webkitExitFullscreen) await document.webkitExitFullscreen();
        syncFromDocument();
        return !panelFullscreen.active;
    } catch {
        syncFromDocument();
        return false;
    }
}

export async function togglePanelFullscreen() {
    ensureListening();
    if (panelFullscreen.active) return exitPanelFullscreen();
    return enterPanelFullscreen();
}

/** Arranca el listener global (llamar una vez desde una vista del panel). */
export function ensurePanelFullscreenReady() {
    ensureListening();
}
